/**
 * AdminConfigPanel — visible only to admin/qx/dev users in the Settings tab.
 *
 * Two groups, split the same way saved configuration sets are (see
 * backend/config_sets.py): settings that decide what a NEWLY GENERATED roster
 * looks like, and shortcuts that take effect immediately on the roster already
 * on screen. Each group carries its own save/share/adopt bar right where its
 * settings live, so what a save captures is whatever is visibly inside it —
 * no separate "saved configurations" screen needed to explain that.
 *
 * Above both: what is fixed and NOT editable (shift geometry), shown first so
 * nobody hunts for a knob that was never going to be there.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { apiClient } from "@/api/client";
import { useNotification } from "@/contexts/NotificationContext";
import {
  generateTimeSlots,
  MORNING_START_HOUR,
  NIGHT_START_HOUR,
  SLOT_DURATION_MINUTES,
} from "@/config/modeConfig";
import { DefaultSOSTable } from "@/components/DefaultSOSTable";
import { QuickActionsSection } from "@/components/admin/QuickActionsSection";
import { RosterTemplateSection } from "@/components/admin/RosterTemplateSection";
import { SavedSetsBar } from "@/components/admin/SavedSetsBar";
import type {
  OTPresetsConfig,
  OTPresetItem,
  GenerationDefaultsConfig,
  SOSDefaultsConfig,
  SOSOfficerData,
  OperationMode,
  ShiftType,
  BreakRulesConfig,
  OTBreakRuleConfig,
  HWDefaultsConfig,
  ShiftConfig,
  ConfigSetKind,
} from "@/types/api";

// ─── Shared helpers ───────────────────────────────────────────────────────────

function slotsToHHMM(slots: number, slotDurationMin: number): string {
  const totalMin = slots * slotDurationMin;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function generateSlotOptions(maxSlots: number, slotDurationMin: number): { value: number; label: string }[] {
  return Array.from({ length: maxSlots }, (_, i) => ({
    value: i + 1,
    label: slotsToHHMM(i + 1, slotDurationMin),
  }));
}

/** Compute the default night HW end time for a given SN and operation mode. */
function computeNightHWDefault(sn: number, mode: OperationMode): string {
  const offsets = mode === "arrival" ? [180, 90, 120, 150] : [150, 120, 150, 180];
  const offsetMin = offsets[sn % 4];
  const totalMin = NIGHT_START_HOUR * 60 + offsetMin;
  const h = Math.floor(totalMin / 60) % 24;
  const m = totalMin % 60;
  return `${String(h).padStart(2, "0")}${String(m).padStart(2, "0")}`;
}

/**
 * Compute the default morning HW end time. Every 4th SN is a Group Leader
 * (same GL_DIVISOR convention as the roster wizard) and works the ad-hoc
 * counter 75 min in rather than the usual 30 — SN4, SN8, SN12… end at 1115,
 * everyone else at 1030.
 */
function computeMorningHWDefault(sn: number): string {
  const isGL = sn % 4 === 0;
  const totalMin = MORNING_START_HOUR * 60 + (isGL ? 75 : 30);
  const h = Math.floor(totalMin / 60) % 24;
  const m = totalMin % 60;
  return `${String(h).padStart(2, "0")}${String(m).padStart(2, "0")}`;
}

// ─── Shared UI atoms ─────────────────────────────────────────────────────────

function Section({
  title,
  subtitle,
  defaultOpen = false,
  children,
}: {
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 12, overflow: "hidden" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          background: open ? "#f9fafb" : "white",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, color: "#374151" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{subtitle}</div>}
        </div>
        <span style={{ fontSize: 12, color: "#6b7280", flexShrink: 0, marginLeft: 8 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div style={{ padding: "0 16px 16px", borderTop: "1px solid #e5e7eb" }}>
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Wraps a set of related Sections. The SavedSetsBar for this group lives
 * inside the same header banner as the title — not as a separate card below
 * it — so it's visually unambiguous that "Save" captures everything in this
 * group, not just whatever happens to be nearby.
 */
function Group({
  title,
  subtitle,
  kind,
  children,
}: {
  title: string;
  subtitle: string;
  kind: ConfigSetKind;
  children: React.ReactNode;
}) {
  return (
    <div style={{ border: "1px solid #c7d2fe", borderRadius: 12, marginBottom: 20, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px 10px", background: "#eef2ff", borderBottom: "1px solid #c7d2fe" }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: "#312e81" }}>{title}</div>
        <div style={{ fontSize: 12, color: "#4c51bf", marginTop: 2, marginBottom: 10 }}>{subtitle}</div>
        <SavedSetsBar kind={kind} embedded />
      </div>
      <div style={{ padding: 14 }}>{children}</div>
    </div>
  );
}

function LockedConfigNotice() {
  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: "10px 12px",
        marginBottom: 20,
        fontSize: 12,
        color: "#6b7280",
        lineHeight: 1.6,
      }}
    >
      <span style={{ fontSize: 14, lineHeight: 1.4 }} aria-hidden>🔒</span>
      <div>
        <div style={{ color: "#374151", fontWeight: 600, marginBottom: 2 }}>
          Fixed — not editable below
        </div>
        Morning 10:00–22:00 · Night 22:00–10:00 · 48 slots × 15 min. The scheduler, the
        roster grid and the Excel template all assume this layout, so a mismatch here
        would shift every assignment without warning.
      </div>
    </div>
  );
}

function SaveButton({ onClick, saving }: { onClick: () => void; saving: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={saving}
      style={{
        padding: "6px 16px",
        background: saving ? "#9ca3af" : "#667eea",
        color: "white",
        border: "none",
        borderRadius: 6,
        cursor: saving ? "not-allowed" : "pointer",
        fontSize: 13,
        fontWeight: 600,
      }}
    >
      {saving ? "Saving…" : "Save"}
    </button>
  );
}

/** Key-order-independent value comparison, so a config loaded from the DB and
 *  the same config built in code compare equal. */
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}

/**
 * Reset-to-defaults control that always says what it did.
 *
 * Resetting a section that is already on its defaults changes nothing on
 * screen, which reads as a dead button — so the outcome is reported either
 * way, as a neutral notice rather than a success banner: nothing has been
 * saved yet, only the form has moved.
 */
function ResetButton({
  onClick,
  what,
  current,
  defaults,
}: {
  onClick: () => void;
  /** Section name for the notification, e.g. "SOS defaults". */
  what: string;
  /** The section's current value and its built-in default, compared to tell
   *  "reset" from "already on defaults". Omit to always report a reset. */
  current?: unknown;
  defaults?: unknown;
}) {
  const { notifyInfo } = useNotification();
  const alreadyDefault =
    current !== undefined && defaults !== undefined &&
    stableStringify(current) === stableStringify(defaults);

  return (
    <button
      onClick={() => {
        if (alreadyDefault) {
          notifyInfo(`${what} are already on the built-in defaults.`, false);
          return;
        }
        onClick();
        notifyInfo(`${what} reset to the built-in defaults — press Save to apply.`, false);
      }}
      style={{
        padding: "6px 12px",
        background: "transparent",
        color: "#6b7280",
        border: "1px solid #d1d5db",
        borderRadius: 6,
        cursor: "pointer",
        fontSize: 12,
      }}
    >
      Reset to defaults
    </button>
  );
}

const tabBtnStyle = (active: boolean): React.CSSProperties => ({
  padding: "4px 12px",
  border: `1px solid ${active ? "#667eea" : "#d1d5db"}`,
  borderRadius: 4,
  background: active ? "#eff6ff" : "white",
  color: active ? "#667eea" : "#374151",
  fontWeight: active ? 600 : 400,
  cursor: "pointer",
  fontSize: 13,
});

// ─── SOS Defaults Section ─────────────────────────────────────────────────────

const SOS_DEFAULT_EMPTY: SOSDefaultsConfig = {
  arrival_morning: [],
  arrival_night: [],
  departure_morning: [],
  departure_night: [],
};

function SOSDefaultsSection() {
  const [config, setConfig] = useState<SOSDefaultsConfig>(SOS_DEFAULT_EMPTY);
  const [mode, setMode] = useState<OperationMode>("arrival");
  const [shift, setShift] = useState<ShiftType>("night");
  const [saving, setSaving] = useState(false);
  // Bumped on reset: DefaultSOSTable seeds its rows once, so it has to be
  // remounted for a reset to show up in the table the user is looking at.
  const [tableGeneration, setTableGeneration] = useState(0);
  const { notifySuccess, notifyError } = useNotification();

  useEffect(() => {
    apiClient.adminConfig.getSOSDefaults().then(setConfig).catch(() => {});
  }, []);

  const configKey = `${mode}_${shift}` as keyof SOSDefaultsConfig;

  const handleChange = useCallback(
    (officers: SOSOfficerData[]) => {
      setConfig((prev) => ({ ...prev, [configKey]: officers }));
    },
    [configKey],
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.adminConfig.setSOSDefaults(config);
      notifySuccess("SOS defaults saved.");
    } catch {
      notifyError("Failed to save SOS defaults.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        {(["arrival", "departure"] as OperationMode[]).map((m) => (
          <button key={m} style={tabBtnStyle(mode === m)} onClick={() => setMode(m)}>
            {m === "arrival" ? "Arrival" : "Departure"}
          </button>
        ))}
        <span style={{ color: "#d1d5db", alignSelf: "center" }}>·</span>
        {(["morning", "night"] as ShiftType[]).map((s) => (
          <button key={s} style={tabBtnStyle(shift === s)} onClick={() => setShift(s)}>
            {s === "morning" ? "Morning" : "Night"}
          </button>
        ))}
      </div>
      <div style={{ marginTop: 12, overflowX: "auto" }}>
        <DefaultSOSTable
          key={`${mode}-${shift}-${tableGeneration}`}
          shiftType={shift}
          operationMode={mode}
          initialOfficers={config[configKey] as SOSOfficerData[]}
          onChange={handleChange}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <ResetButton
          what="SOS defaults"
          current={config[configKey] ?? []}
          defaults={[]}
          onClick={() => {
            setConfig((prev) => ({ ...prev, [configKey]: [] }));
            setTableGeneration((n) => n + 1);
          }}
        />
        <SaveButton onClick={handleSave} saving={saving} />
      </div>
      <p style={{ fontSize: 11, color: "#9ca3af", margin: "8px 0 0" }}>
        These officers pre-populate the SOS table when generating a {shift} {mode} roster.
      </p>
    </>
  );
}

// ─── OT Presets Section ───────────────────────────────────────────────────────

const OT_PRESETS_DEFAULT: OTPresetsConfig = {
  morning: [
    { label: "1130-1530 (11-4pm)", start: "1130", end: "1530" },
    { label: "1630-2030 (4-9pm)", start: "1630", end: "2030" },
  ],
  night: [{ label: "0630-1000 (6-11am)", start: "0630", end: "1000" }],
};

function OTPresetsSection() {
  const [config, setConfig] = useState<OTPresetsConfig>(OT_PRESETS_DEFAULT);
  const [saving, setSaving] = useState(false);
  const { notifySuccess, notifyError } = useNotification();

  useEffect(() => {
    apiClient.adminConfig.getOTPresets().then(setConfig).catch(() => {});
  }, []);

  const morningSlots = generateTimeSlots("morning", 2);
  const nightSlots = generateTimeSlots("night", 2);

  const updatePreset = (shiftKey: "morning" | "night", idx: number, field: keyof OTPresetItem, value: string) => {
    setConfig((prev) => {
      const list = [...prev[shiftKey]];
      list[idx] = { ...list[idx], [field]: value };
      if (field === "start" || field === "end") {
        list[idx].label = `${list[idx].start}-${list[idx].end}`;
      }
      return { ...prev, [shiftKey]: list };
    });
  };

  const addPreset = (shiftKey: "morning" | "night") => {
    const slots = shiftKey === "morning" ? morningSlots : nightSlots;
    const defaultStart = slots[4] ?? "1000";
    const defaultEnd = slots[12] ?? "1200";
    setConfig((prev) => ({
      ...prev,
      [shiftKey]: [...prev[shiftKey], { label: `${defaultStart}-${defaultEnd}`, start: defaultStart, end: defaultEnd }],
    }));
  };

  const removePreset = (shiftKey: "morning" | "night", idx: number) => {
    setConfig((prev) => ({ ...prev, [shiftKey]: prev[shiftKey].filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.adminConfig.setOTPresets(config);
      notifySuccess("OT presets saved.");
    } catch {
      notifyError("Failed to save OT presets.");
    } finally {
      setSaving(false);
    }
  };

  // Table layout with a horizontal-scroll wrapper: each preset stays on one
  // line and columns stay aligned, instead of wrapping onto a second line on
  // narrow phone widths.
  const cellStyle: React.CSSProperties = { padding: "4px 4px", verticalAlign: "middle" };

  const renderPresetList = (shiftKey: "morning" | "night") => {
    const slots = shiftKey === "morning" ? morningSlots : nightSlots;
    const list = config[shiftKey];
    return (
      <div style={{ flex: "1 1 300px", minWidth: 280 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
          {shiftKey === "morning" ? "Morning" : "Night"} presets
        </div>
        {list.length === 0 ? (
          <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 6 }}>No presets — add one below.</div>
        ) : (
          <div
            style={{
              overflowX: "auto",
              WebkitOverflowScrolling: "touch" as React.CSSProperties["WebkitOverflowScrolling"],
              border: "1px solid #e5e7eb",
              borderRadius: 6,
              marginBottom: 8,
            }}
          >
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 300, fontSize: 13 }}>
              <colgroup>
                <col style={{ width: "auto" }} />
                <col style={{ width: 78 }} />
                <col style={{ width: 16 }} />
                <col style={{ width: 78 }} />
                <col style={{ width: 34 }} />
              </colgroup>
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  <th style={{ ...cellStyle, textAlign: "left", fontWeight: 600, fontSize: 11, color: "#9ca3af", borderBottom: "1px solid #e5e7eb" }}>
                    Label
                  </th>
                  <th colSpan={3} style={{ ...cellStyle, textAlign: "center", fontWeight: 600, fontSize: 11, color: "#9ca3af", borderBottom: "1px solid #e5e7eb" }}>
                    Time range
                  </th>
                  <th style={{ ...cellStyle, borderBottom: "1px solid #e5e7eb" }} />
                </tr>
              </thead>
              <tbody>
                {list.map((p, idx) => (
                  <tr key={idx} style={{ background: idx % 2 === 0 ? "white" : "#fafafa" }}>
                    <td style={cellStyle}>
                      <input
                        value={p.label}
                        onChange={(e) => updatePreset(shiftKey, idx, "label", e.target.value)}
                        placeholder="Label"
                        style={{ width: "100%", boxSizing: "border-box", padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: 4, fontSize: 13 }}
                      />
                    </td>
                    <td style={cellStyle}>
                      <select
                        value={p.start}
                        onChange={(e) => updatePreset(shiftKey, idx, "start", e.target.value)}
                        style={{ width: "100%", padding: "4px 2px", border: "1px solid #d1d5db", borderRadius: 4, fontSize: 13, fontVariantNumeric: "tabular-nums" }}
                      >
                        {slots.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{ ...cellStyle, textAlign: "center", color: "#9ca3af" }}>–</td>
                    <td style={cellStyle}>
                      <select
                        value={p.end}
                        onChange={(e) => updatePreset(shiftKey, idx, "end", e.target.value)}
                        style={{ width: "100%", padding: "4px 2px", border: "1px solid #d1d5db", borderRadius: 4, fontSize: 13, fontVariantNumeric: "tabular-nums" }}
                      >
                        {slots.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{ ...cellStyle, textAlign: "center" }}>
                      <button
                        onClick={() => removePreset(shiftKey, idx)}
                        title="Remove this preset"
                        style={{ padding: "4px 6px", background: "none", border: "1px solid #fca5a5", borderRadius: 4, color: "#ef4444", cursor: "pointer", fontSize: 13, lineHeight: 1 }}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <button
          onClick={() => addPreset(shiftKey)}
          style={{ padding: "4px 10px", background: "none", border: "1px dashed #9ca3af", borderRadius: 4, color: "#6b7280", cursor: "pointer", fontSize: 12 }}
        >
          + Add preset
        </button>
      </div>
    );
  };

  return (
    <>
      <div style={{ marginTop: 12, display: "flex", gap: 24, flexWrap: "wrap" }}>
        {renderPresetList("morning")}
        {renderPresetList("night")}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <ResetButton
          what="OT presets"
          current={config}
          defaults={OT_PRESETS_DEFAULT}
          onClick={() => setConfig(OT_PRESETS_DEFAULT)}
        />
        <SaveButton onClick={handleSave} saving={saving} />
      </div>
      <p style={{ fontSize: 11, color: "#9ca3af", margin: "8px 0 0" }}>
        These presets appear as quick-fill buttons when adding OT officers.
      </p>
    </>
  );
}

// ─── Generation defaults + HW defaults (consumed by Main Officers Section) ───

const GENERATION_DEFAULTS_DEFAULT: GenerationDefaultsConfig = {
  reported_counts: {
    "morning-arrival": 18,
    "morning-departure": 18,
    "night-arrival": 22,
    "night-departure": 22,
  },
};

const HW_DEFAULTS_EMPTY: HWDefaultsConfig = {
  arrival_morning: {},
  arrival_night: {},
  departure_morning: {},
  departure_night: {},
};

// ─── Break Allocation Section ─────────────────────────────────────────────────

interface ThresholdRow {
  id: string;
  min_slots: number;
  pattern: number[];
}

let _thresholdRowId = 0;
const newThresholdId = () => `t-${++_thresholdRowId}`;

const OT_BREAK_RULE_DEFAULT: OTBreakRuleConfig = {
  work_before_break_min: 60,
  counter_after_break_min: 45,
  stays_past_hoto_min: { morning: 0, night: 30 },
};

const BREAK_RULES_DEFAULT: BreakRulesConfig = {
  thresholds: [
    { min_slots: 36, pattern: [2, 3, 3] },
    { min_slots: 28, pattern: [2, 3] },
    { min_slots: 20, pattern: [3] },
    { min_slots: 11, pattern: [2] },
  ],
  ot: OT_BREAK_RULE_DEFAULT,
};

/** Choices for the OT break-timing minutes: 15 min to 3 h. */
const OT_RULE_MINUTES = Array.from({ length: 12 }, (_, i) => (i + 1) * SLOT_DURATION_MINUTES);
/** Choices for how long OT stays past HOTO: none to 2 h. */
const OT_OVERSTAY_MINUTES = Array.from({ length: 9 }, (_, i) => i * SLOT_DURATION_MINUTES);

const MAX_BREAKS_PER_ROW = 10;
const MAX_THRESHOLD_ROWS = 100;

function BreakAllocationSection() {
  const slotDuration = SLOT_DURATION_MINUTES;
  const [rows, setRows] = useState<ThresholdRow[]>([]);
  const [otRule, setOtRule] = useState<OTBreakRuleConfig>(OT_BREAK_RULE_DEFAULT);
  const [saving, setSaving] = useState(false);
  const { notifySuccess, notifyError } = useNotification();

  useEffect(() => {
    const loadRows = (cfg: BreakRulesConfig) => {
      const sorted = [...(cfg.thresholds ?? [])].sort((a, b) => b.min_slots - a.min_slots);
      setRows(sorted.map((t) => ({ id: newThresholdId(), min_slots: t.min_slots, pattern: [...t.pattern] })));
      setOtRule({
        ...OT_BREAK_RULE_DEFAULT,
        ...cfg.ot,
        stays_past_hoto_min: { ...OT_BREAK_RULE_DEFAULT.stays_past_hoto_min, ...cfg.ot?.stays_past_hoto_min },
      });
    };
    apiClient.adminConfig
      .getBreakRules()
      .then(loadRows)
      .catch(() => loadRows(BREAK_RULES_DEFAULT));
  }, []);

  const durationOpts = generateSlotOptions(48, slotDuration);
  const breakOpts = generateSlotOptions(24, slotDuration);

  const updateMinSlots = (id: string, slots: number) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, min_slots: slots } : r)));

  const updateBreak = (id: string, bi: number, slots: number) =>
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const p = [...r.pattern];
        p[bi] = slots;
        return { ...r, pattern: p };
      }),
    );

  const addBreak = (id: string) =>
    setRows((prev) =>
      prev.map((r) =>
        r.id !== id || r.pattern.length >= MAX_BREAKS_PER_ROW
          ? r
          : { ...r, pattern: [...r.pattern, breakOpts[1]?.value ?? 2] },
      ),
    );

  const removeBreak = (id: string, bi: number) =>
    setRows((prev) =>
      prev.map((r) =>
        r.id !== id ? r : { ...r, pattern: r.pattern.filter((_, i) => i !== bi) },
      ),
    );

  const addRow = () => {
    if (rows.length >= MAX_THRESHOLD_ROWS) return;
    setRows((prev) => [{ id: newThresholdId(), min_slots: 1, pattern: [] }, ...prev]);
  };

  const removeRow = (id: string) => setRows((prev) => prev.filter((r) => r.id !== id));

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.adminConfig.setBreakRules({
        thresholds: rows.map((r) => ({ min_slots: r.min_slots, pattern: r.pattern })),
        ot: otRule,
      });
      notifySuccess("Break allocation saved.");
    } catch {
      notifyError("Failed to save break allocation.");
    } finally {
      setSaving(false);
    }
  };

  const defaultThresholds = useMemo(
    () => [...BREAK_RULES_DEFAULT.thresholds].sort((a, b) => b.min_slots - a.min_slots),
    [],
  );
  const currentThresholds = rows.map((r) => ({ min_slots: r.min_slots, pattern: r.pattern }));

  const handleReset = () => {
    setRows(defaultThresholds.map((t) => ({ id: newThresholdId(), min_slots: t.min_slots, pattern: [...t.pattern] })));
    setOtRule(OT_BREAK_RULE_DEFAULT);
  };

  const fmtMinutes = (m: number) => slotsToHHMM(m / SLOT_DURATION_MINUTES, SLOT_DURATION_MINUTES);

  const selStyle: React.CSSProperties = {
    padding: "4px 5px",
    border: "1px solid #d1d5db",
    borderRadius: 4,
    fontSize: 13,
    background: "white",
    fontVariantNumeric: "tabular-nums",
  };

  // Trash button and its header/footer spacer share this width so the
  // duration column lines up under its header on every row — without it the
  // header sits flush left while every row's dropdown is pushed right by the
  // trash button, and the two drift out of register.
  const removeColWidth = 27;

  // Smallest configured threshold — used for the "shorter shifts" footer label
  const minThreshold = rows.length > 0 ? Math.min(...rows.map((r) => r.min_slots)) : null;
  // Shifts within the user's Max ctr duration never get a break; the bottom row takes over past it.
  const minLabel = minThreshold ? "Within max ctr duration" : "All shifts";

  return (
    <>
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 10 }}>
          Breaks are automatically scheduled based on shift length ({slotDuration}-min slots, times shown as HH:MM).
          Rows matched top-to-bottom — highest threshold wins. Breaks start just past each
          user's Max ctr duration (2:30 unless they change it): a shift within it gets no
          break, and the bottom row then applies from there, whatever its own threshold.
        </div>

        {/* Horizontal-scroll wrapper: each row stays on one line */}
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" as React.CSSProperties["WebkitOverflowScrolling"] }}>
          <div style={{ minWidth: "max-content" }}>
            {/* Column headers — leading spacer matches the trash button column
                below so "If shift is at least" sits directly above the
                duration dropdown, not above the trash button. */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                paddingBottom: 6,
                borderBottom: "1px solid #e5e7eb",
                marginBottom: 4,
                fontSize: 12,
                fontWeight: 600,
                color: "#6b7280",
              }}
            >
              <div style={{ width: removeColWidth, flexShrink: 0 }} />
              <div style={{ width: 116, flexShrink: 0 }}>If shift is at least</div>
              <div>Breaks to auto-assign</div>
            </div>

            {/* Data rows — each on one line, no wrapping */}
            {rows.map((row) => (
              <div
                key={row.id}
                style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}
              >
                {/* Remove row — trash icon on the LEFT */}
                <button
                  onClick={() => removeRow(row.id)}
                  title="Remove this row"
                  style={{
                    width: removeColWidth,
                    height: 27,
                    boxSizing: "border-box",
                    padding: 0,
                    background: "none",
                    border: "1px solid #fca5a5",
                    borderRadius: 4,
                    color: "#ef4444",
                    cursor: "pointer",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg viewBox="0 0 16 16" fill="currentColor" width="13" height="13">
                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                  </svg>
                </button>

                {/* Working duration dropdown */}
                <select
                  value={row.min_slots}
                  onChange={(e) => updateMinSlots(row.id, Number(e.target.value))}
                  style={{ ...selStyle, width: 116, flexShrink: 0 }}
                >
                  {durationOpts.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>

                {/* No breaks yet placeholder */}
                {row.pattern.length === 0 && (
                  <span style={{ fontSize: 12, color: "#9ca3af", marginRight: 4 }}>— no break yet —</span>
                )}

                {/* Break pills */}
                {row.pattern.map((bSlots, bi) => (
                  <div key={bi} style={{ display: "inline-flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
                    <select
                      value={bSlots}
                      onChange={(e) => updateBreak(row.id, bi, Number(e.target.value))}
                      style={{ ...selStyle, width: 84 }}
                    >
                      {breakOpts.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => removeBreak(row.id, bi)}
                      title="Remove this break"
                      style={{ padding: "3px 6px", background: "none", border: "1px solid #fca5a5", borderRadius: 3, color: "#ef4444", cursor: "pointer", fontSize: 11, lineHeight: 1, flexShrink: 0 }}
                    >
                      ✕
                    </button>
                  </div>
                ))}

                {/* Add break */}
                {row.pattern.length < MAX_BREAKS_PER_ROW && (
                  <button
                    onClick={() => addBreak(row.id)}
                    style={{ padding: "3px 10px", background: "none", border: "1px dashed #9ca3af", borderRadius: 3, color: "#6b7280", cursor: "pointer", fontSize: 12, flexShrink: 0 }}
                  >
                    + break
                  </button>
                )}
              </div>
            ))}

            {/* Footer row: "shorter shifts → no break" — same leading spacer
                as the header/data rows so its label lines up under the
                duration column too. */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                paddingTop: 6,
                borderTop: "1px dashed #e5e7eb",
                marginTop: 2,
                fontSize: 12,
                color: "#9ca3af",
                fontStyle: "italic",
              }}
            >
              <div style={{ width: removeColWidth, flexShrink: 0 }} />
              <div style={{ width: 116, flexShrink: 0 }}>{minLabel}</div>
              <div>No break assigned</div>
            </div>
          </div>
        </div>

        {rows.length < MAX_THRESHOLD_ROWS && (
          <button
            onClick={addRow}
            style={{ marginTop: 8, padding: "4px 12px", background: "none", border: "1px dashed #9ca3af", borderRadius: 4, color: "#6b7280", cursor: "pointer", fontSize: 12 }}
          >
            + Add row
          </button>
        )}
      </div>

      {/* OT break timing — where an OT officer's break may start (backend/ot_break_window.py) */}
      <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid #e5e7eb" }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>OT break timing</div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12, color: "#374151" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            First break no earlier than
            <select
              value={otRule.work_before_break_min}
              onChange={(e) => setOtRule((r) => ({ ...r, work_before_break_min: Number(e.target.value) }))}
              style={selStyle}
            >
              {OT_RULE_MINUTES.map((m) => <option key={m} value={m}>{fmtMinutes(m)}</option>)}
            </select>
            after OT starts
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            Back to at least
            <select
              value={otRule.counter_after_break_min}
              onChange={(e) => setOtRule((r) => ({ ...r, counter_after_break_min: Number(e.target.value) }))}
              style={selStyle}
            >
              {OT_RULE_MINUTES.map((m) => <option key={m} value={m}>{fmtMinutes(m)}</option>)}
            </select>
            of counter after a break
          </label>
        </div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12, color: "#374151", marginTop: 8 }}>
          {(["morning", "night"] as const).map((shiftKey) => (
            <label key={shiftKey} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {shiftKey === "morning" ? "Morning" : "Night"} OT ending at HOTO stays
              <select
                value={otRule.stays_past_hoto_min[shiftKey]}
                onChange={(e) => setOtRule((r) => ({
                  ...r, stays_past_hoto_min: { ...r.stays_past_hoto_min, [shiftKey]: Number(e.target.value) },
                }))}
                style={selStyle}
              >
                {OT_OVERSTAY_MINUTES.map((m) => <option key={m} value={m}>{m === 0 ? "no longer" : `${fmtMinutes(m)} longer`}</option>)}
              </select>
            </label>
          ))}
        </div>
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
          Counted to when the officer leaves — for OT staying past HOTO (the next team plans that
          time), to that time. The opt-in last break is the one exception: it ends as the officer leaves.
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <ResetButton
          what="Break allocation rules"
          current={{ thresholds: currentThresholds, ot: otRule }}
          defaults={{ thresholds: defaultThresholds, ot: OT_BREAK_RULE_DEFAULT }}
          onClick={handleReset}
        />
        <SaveButton onClick={handleSave} saving={saving} />
      </div>
    </>
  );
}

// ─── Main Officers Section (SN totals + car-zone default + HW end times) ─────

/**
 * Fallbacks matching the built-in templates. Departure night genuinely has fewer
 * main officers than departure morning, which is why this is keyed per mode+shift
 * rather than per mode.
 */
const SHIFT_CONFIG_DEFAULT: ShiftConfig = {
  total_sns: {
    arrival_morning: 40, arrival_night: 40,
    departure_morning: 36, departure_night: 32,
  },
};

/**
 * Per-row max for "pre-checked as reported" before this admin panel ever
 * touches it — mirrors the historical car-zone headcount ceilings so a saved
 * value never jumps on first load, even though the live cap is always
 * whatever Total SN is set to (a checked count can never exceed the total).
 */
const REPORTED_COUNT_FALLBACK_MAX: Record<string, number> = {
  "morning-arrival": 40,
  "morning-departure": 36,
  "night-arrival": 40,
  "night-departure": 36,
};

/**
 * Main Officers — merges what used to be three separate sections (SN totals,
 * car-zone headcount default, HW counter defaults) into one per-SN table,
 * the same shape as the "Officer Details" table in step 3 of the roster
 * wizard: a checkbox beside each SN, grouped in fours. The car-zone default
 * is a subset of the SN total by definition, so showing them side by side —
 * and deriving the checked count directly from which boxes are ticked —
 * removes a second number that had to be kept in sync by hand.
 */
function MainOfficersSection() {
  const [shiftConfig, setShiftConfig] = useState<ShiftConfig>(SHIFT_CONFIG_DEFAULT);
  const [genDefaults, setGenDefaults] = useState<GenerationDefaultsConfig>(GENERATION_DEFAULTS_DEFAULT);
  const [hwConfig, setHwConfig] = useState<HWDefaultsConfig>(HW_DEFAULTS_EMPTY);
  const [mode, setMode] = useState<OperationMode>("arrival");
  const [shift, setShift] = useState<ShiftType>("night");
  const [saving, setSaving] = useState(false);
  const { notifySuccess, notifyError } = useNotification();

  // Morning uses 15-min steps, not the usual 30 — the GL default (start + 75
  // min) lands on a quarter-hour, and a 30-min-only option list would silently
  // fall back to its first option since 1115 isn't one of the choices.
  const morningSlots = generateTimeSlots("morning", 1);
  const nightSlots = generateTimeSlots("night", 2);

  useEffect(() => {
    apiClient.adminConfig.getShiftConfig().then(setShiftConfig).catch(() => {});
    apiClient.adminConfig.getGenerationDefaults().then(setGenDefaults).catch(() => {});
    apiClient.adminConfig.getHWDefaults().then(setHwConfig).catch(() => {});
  }, []);

  const shiftKey = `${mode}_${shift}`; // ShiftConfig / HWDefaults key format
  const genKey = `${shift}-${mode}`; // GenerationDefaults key format

  const total = shiftConfig.total_sns?.[shiftKey] ?? SHIFT_CONFIG_DEFAULT.total_sns[shiftKey] ?? 40;
  const reportedCount = Math.min(genDefaults.reported_counts[genKey] ?? 0, total);
  const savedHwMap: Record<string, string> = hwConfig[shiftKey as keyof HWDefaultsConfig] ?? {};
  const timeSlots = shift === "night" ? nightSlots : morningSlots;

  const getFormulaDefault = (sn: number) =>
    shift === "night" ? computeNightHWDefault(sn, mode) : computeMorningHWDefault(sn);

  const rows = Array.from({ length: total }, (_, i) => {
    const sn = i + 1;
    const snStr = String(sn);
    const computed = getFormulaDefault(sn);
    const saved = savedHwMap[snStr];
    return { sn, snStr, hwValue: saved ?? computed, isOverride: !!saved, checked: sn <= reportedCount };
  });

  const setTotal = (rawValue: string) => {
    const n = parseInt(rawValue, 10);
    if (!Number.isInteger(n)) return;
    const clamped = Math.max(1, Math.min(200, n));
    setShiftConfig((prev) => ({ ...prev, total_sns: { ...prev.total_sns, [shiftKey]: clamped } }));
    // A checked count can never exceed the total it's a subset of.
    setGenDefaults((prev) => ({
      ...prev,
      reported_counts: { ...prev.reported_counts, [genKey]: Math.min(prev.reported_counts[genKey] ?? 0, clamped) },
    }));
  };

  // Checking SN n fills every box up to and including it; unchecking it
  // clears from n onward — the table's only meaningful state is "reported
  // up to SN n", same as the leading-N convention the wizard reads it with.
  const setReportedUpTo = (sn: number, checked: boolean) => {
    const next = Math.max(0, Math.min(checked ? sn : sn - 1, total));
    setGenDefaults((prev) => ({ ...prev, reported_counts: { ...prev.reported_counts, [genKey]: next } }));
  };

  const updateHwTime = (snStr: string, value: string) => {
    setHwConfig((prev) => ({
      ...prev,
      [shiftKey]: { ...(prev[shiftKey as keyof HWDefaultsConfig] ?? {}), [snStr]: value },
    }));
  };

  const handleSave = async () => {
    const hwRec: Record<string, string> = {};
    rows.forEach((r) => {
      hwRec[r.snStr] = savedHwMap[r.snStr] ?? getFormulaDefault(r.sn);
    });
    const updatedHw = { ...hwConfig, [shiftKey]: hwRec };
    setSaving(true);
    try {
      await Promise.all([
        apiClient.adminConfig.setShiftConfig(shiftConfig),
        apiClient.adminConfig.setGenerationDefaults(genDefaults),
        apiClient.adminConfig.setHWDefaults(updatedHw),
      ]);
      setHwConfig(updatedHw);
      notifySuccess("Main officer settings saved.");
    } catch {
      notifyError("Failed to save main officer settings.");
    } finally {
      setSaving(false);
    }
  };

  const defaultMainSettings = {
    total_sns: SHIFT_CONFIG_DEFAULT.total_sns[shiftKey] ?? 40,
    reported: GENERATION_DEFAULTS_DEFAULT.reported_counts[genKey] ?? REPORTED_COUNT_FALLBACK_MAX[genKey] ?? 0,
    hw: {},
  };
  const currentMainSettings = {
    total_sns: shiftConfig.total_sns[shiftKey],
    reported: genDefaults.reported_counts[genKey],
    hw: savedHwMap,
  };

  const handleReset = () => {
    setShiftConfig((prev) => ({ ...prev, total_sns: { ...prev.total_sns, [shiftKey]: defaultMainSettings.total_sns } }));
    setGenDefaults((prev) => ({
      ...prev,
      reported_counts: { ...prev.reported_counts, [genKey]: defaultMainSettings.reported },
    }));
    setHwConfig((prev) => ({ ...prev, [shiftKey]: {} }));
  };

  return (
    <>
      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        {(["arrival", "departure"] as OperationMode[]).map((m) => (
          <button key={m} style={tabBtnStyle(mode === m)} onClick={() => setMode(m)}>
            {m === "arrival" ? "Arrival" : "Departure"}
          </button>
        ))}
        <span style={{ color: "#d1d5db", alignSelf: "center" }}>·</span>
        {(["morning", "night"] as ShiftType[]).map((s) => (
          <button key={s} style={tabBtnStyle(shift === s)} onClick={() => setShift(s)}>
            {s === "morning" ? "Morning" : "Night"}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px 10px", marginTop: 14, fontSize: 13 }}>
        <span style={{ color: "#374151", fontWeight: 600 }}>Total main officers (SN)</span>
        <input
          type="number"
          min={1}
          max={200}
          step={1}
          value={total}
          onChange={(e) => setTotal(e.target.value)}
          style={{ width: 64, padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: 4, fontSize: 13, textAlign: "center" }}
        />
        <span style={{ color: "#d1d5db" }}>·</span>
        <span style={{ color: "#6b7280" }}>
          1st <strong style={{ color: "#667eea" }}>{reportedCount}</strong> rows pre-checked as reported by default
        </span>
      </div>

      <div
        style={{
          marginTop: 10,
          maxHeight: 400,
          overflowX: "auto",
          overflowY: "auto",
          border: "1px solid #e5e7eb",
          borderRadius: 6,
          WebkitOverflowScrolling: "touch" as React.CSSProperties["WebkitOverflowScrolling"],
        }}
      >
        <table style={{ borderCollapse: "collapse", minWidth: "100%", fontSize: 13 }}>
          <thead>
            <tr>
              <th
                style={{
                  position: "sticky",
                  left: 0,
                  top: 0,
                  background: "#f9fafb",
                  zIndex: 3,
                  padding: "6px 12px",
                  borderBottom: "1px solid #e5e7eb",
                  textAlign: "left",
                  fontWeight: 600,
                  fontSize: 12,
                  color: "#6b7280",
                  whiteSpace: "nowrap",
                }}
              >
                SN · reported by default
              </th>
              <th
                style={{
                  position: "sticky",
                  top: 0,
                  background: "#f9fafb",
                  zIndex: 2,
                  padding: "6px 12px",
                  borderBottom: "1px solid #e5e7eb",
                  textAlign: "left",
                  fontWeight: 600,
                  fontSize: 12,
                  color: "#6b7280",
                  whiteSpace: "nowrap",
                }}
              >
                HW End Time ({shift})
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.sn} style={{ background: i % 2 === 0 ? "white" : "#fafafa" }}>
                <td
                  style={{
                    position: "sticky",
                    left: 0,
                    background: i % 2 === 0 ? "white" : "#fafafa",
                    zIndex: 1,
                    padding: "3px 12px",
                    borderBottom: i < rows.length - 1 ? "1px solid #f3f4f6" : "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={row.checked}
                      onChange={(e) => setReportedUpTo(row.sn, e.target.checked)}
                      style={{ width: 15, height: 15, cursor: "pointer", accentColor: "#667eea", flexShrink: 0 }}
                    />
                    <span style={{ fontWeight: 600, color: "#374151" }}>SN{row.sn}</span>
                  </label>
                </td>
                <td
                  style={{
                    padding: "3px 12px",
                    borderBottom: i < rows.length - 1 ? "1px solid #f3f4f6" : "none",
                  }}
                >
                  <select
                    value={row.hwValue}
                    onChange={(e) => updateHwTime(row.snStr, e.target.value)}
                    style={{
                      padding: "3px 6px",
                      border: `1px solid ${row.isOverride ? "#667eea" : "#d1d5db"}`,
                      borderRadius: 4,
                      fontSize: 13,
                      background: row.isOverride ? "#eff6ff" : "transparent",
                      color: row.isOverride ? "#667eea" : "#6b7280",
                      minWidth: 90,
                    }}
                  >
                    {timeSlots.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <ResetButton
          what="Main officer settings"
          current={currentMainSettings}
          defaults={defaultMainSettings}
          onClick={handleReset}
        />
        <SaveButton onClick={handleSave} saving={saving} />
      </div>
    </>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

interface AdminConfigPanelProps {
  /** The roster currently on screen, if any — passed through to Quick Actions
   * so "Apply" knows what it would be applying to. */
  operationMode?: string;
  shiftType?: string;
  generated?: boolean;
  onApplied?: () => void;
}

export function AdminConfigPanel({
  operationMode,
  shiftType,
  generated,
  onApplied,
}: AdminConfigPanelProps) {
  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ borderTop: "2px solid #e5e7eb", paddingTop: 16, marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 15, color: "#374151" }}>Admin Configuration</h3>
      </div>

      <LockedConfigNotice />

      <Group
        title="Roster Generation Settings"
        subtitle="Used the next time a roster is generated — the roster on screen doesn't change until then"
        kind="generation"
      >
        <Section title="Main Officers" defaultOpen>
          <MainOfficersSection />
        </Section>

        <Section title="Roster Template" subtitle="Which counter each main officer works — edit on-screen or via Excel">
          <RosterTemplateSection />
        </Section>

        <Section title="Default SOS Officers" subtitle="Pre-populate the SOS officer table per mode and shift">
          <SOSDefaultsSection />
        </Section>

        <Section title="Break Allocation" subtitle="Auto-assign breaks based on shift length">
          <BreakAllocationSection />
        </Section>
      </Group>

      <Group
        title="Shortcuts"
        subtitle="Take effect immediately on the roster already on screen — no regeneration needed"
        kind="shortcut"
      >
        <Section title="Quick Move/Swap Presets" subtitle="One-click batch operations, applied right here">
          <QuickActionsSection
            operationMode={operationMode}
            shiftType={shiftType}
            generated={generated}
            onApplied={onApplied}
          />
        </Section>

        <Section title="OT Quick-Fill Presets" subtitle="Preset time ranges shown as buttons in the OT tab">
          <OTPresetsSection />
        </Section>
      </Group>
    </div>
  );
}
